import { useLanguage } from '@/context/LanguageContext';
import { Link } from 'react-router-dom';
import { Handshake, Globe, TrendingUp, Users, FileText, ArrowRight, Flag, Building2 } from 'lucide-react';

const FLAGS_IMAGE = 'https://images.unsplash.com/photo-1668734593713-4b5bdc4faab6?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODF8MHwxfHNlYXJjaHwzfHxkaXBsb21hdGljJTIwZmxhZ3MlMjBtZWV0aW5nfGVufDB8fHx8MTc3NTUxMjMzMHww&ixlib=rb-4.1.0&q=85';

export default function BilateralRelations() {
  const { t } = useLanguage();

  const timeline = [
    { year: '1975', event: t('bilateral.timeline_1975') },
    { year: '1982', event: t('bilateral.timeline_1982') },
    { year: '2000', event: t('bilateral.timeline_2000') },
    { year: '2010', event: t('bilateral.timeline_2010') },
    { year: '2025', event: t('bilateral.timeline_2025') },
  ];

  return (
    <div className="min-h-screen bg-white" data-testid="bilateral-page">
      {/* Hero */}
      <div className="relative h-[340px] md:h-[400px] overflow-hidden">
        <img src={FLAGS_IMAGE} alt="Angola Espanha" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-black/55" />
        <div className="relative z-10 flex flex-col items-center justify-center h-full text-center text-white px-4">
          <p className="text-xs uppercase tracking-[0.2em] font-bold text-[#FFD700] mb-3">{t('bilateral.overline')}</p>
          <h1 className="text-4xl md:text-5xl font-bold tracking-tight font-serif">{t('bilateral.title')}</h1>
          <p className="text-base text-white/80 max-w-2xl mt-4">{t('bilateral.subtitle')}</p>
        </div>
      </div>

      {/* Overview + Stats */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
            <div>
              <p className="text-xs uppercase tracking-[0.2em] font-bold text-[#CC0000] mb-3">{t('bilateral.relations_overline')}</p>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 tracking-tight font-serif mb-6">{t('bilateral.overview_title')}</h2>
              <div className="space-y-4 text-gray-600 leading-relaxed">
                <p>{t('bilateral.overview_p1')}</p>
                <p>{t('bilateral.overview_p2')}</p>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              {[
                { value: '1975', label: t('bilateral.stat_year'), icon: Flag },
                { value: '+50K', label: t('bilateral.stat_community'), icon: Users },
                { value: '20+', label: t('bilateral.stat_agreements'), icon: Handshake },
                { value: '5+', label: t('bilateral.stat_sectors'), icon: TrendingUp },
              ].map(({ value, label, icon: Icon }) => (
                <div key={label} className="bg-gray-50 border border-gray-100 rounded-md p-6 text-center hover:shadow-sm transition-all">
                  <Icon size={24} className="text-[#CC0000] mx-auto mb-2" />
                  <p className="text-2xl font-bold text-gray-900">{value}</p>
                  <p className="text-xs text-gray-500 mt-1">{label}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Areas of Cooperation */}
      <section className="py-16 bg-[#F9FAFB]">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 tracking-tight font-serif">{t('bilateral.areas_title')}</h2>
            <div className="w-16 h-1 bg-[#CC0000] mx-auto mt-4" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { icon: TrendingUp, title: t('bilateral.area_economy'), desc: t('bilateral.area_economy_desc') },
              { icon: Globe, title: t('bilateral.area_culture'), desc: t('bilateral.area_culture_desc') },
              { icon: Users, title: t('bilateral.area_education'), desc: t('bilateral.area_education_desc') },
              { icon: Building2, title: t('bilateral.area_energy'), desc: t('bilateral.area_energy_desc') },
              { icon: Handshake, title: t('bilateral.area_politics'), desc: t('bilateral.area_politics_desc') },
              { icon: Flag, title: t('bilateral.area_tourism'), desc: t('bilateral.area_tourism_desc') },
            ].map(({ icon: Icon, title, desc }) => (
              <div key={title} className="bg-white p-6 border border-gray-100 rounded-md hover:shadow-md hover:-translate-y-1 transition-all">
                <div className="w-12 h-12 bg-red-50 rounded-full flex items-center justify-center mb-4">
                  <Icon size={20} className="text-[#CC0000]" />
                </div>
                <h3 className="font-bold text-gray-900 mb-2">{title}</h3>
                <p className="text-sm text-gray-500 leading-relaxed">{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 tracking-tight font-serif">{t('bilateral.timeline_title')}</h2>
            <div className="w-16 h-1 bg-[#CC0000] mx-auto mt-4" />
          </div>
          <div className="max-w-2xl mx-auto space-y-0">
            {timeline.map(({ year, event }, i) => (
              <div key={year} className="flex gap-4 items-start">
                <div className="flex flex-col items-center">
                  <div className="w-10 h-10 bg-[#CC0000] rounded-full flex items-center justify-center text-white text-xs font-bold flex-shrink-0">{year}</div>
                  {i < timeline.length - 1 && <div className="w-0.5 h-16 bg-gray-200" />}
                </div>
                <div className="pb-8 pt-2">
                  <p className="text-sm text-gray-700">{event}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-[#0A0A0A]">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold text-white font-serif mb-4">{t('bilateral.cta_title')}</h2>
          <p className="text-gray-400 mb-8 max-w-xl mx-auto">{t('bilateral.cta_desc')}</p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/investir-em-angola" className="inline-flex items-center gap-2 px-6 py-3 bg-[#CC0000] text-white font-medium rounded-sm hover:bg-[#A30000] transition-colors" data-testid="bilateral-invest-btn">
              {t('bilateral.cta_invest')} <ArrowRight size={16} />
            </Link>
            <Link to="/contactos" className="inline-flex items-center gap-2 px-6 py-3 border-2 border-white/30 text-white font-medium rounded-sm hover:bg-white/10 transition-colors" data-testid="bilateral-contact-btn">
              {t('bilateral.cta_contact')}
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
